import { AfterViewInit, Component, EventEmitter, Input, OnInit, Optional, Output, ViewChild } from '@angular/core';
import { UserService, OrganizationService, AppService, HelperService, SupplementProtocolService, SupplementService, StorageService, CameraService, TranslateConfigService } from 'src/app/services';
import { ModalController, IonSelect, AlertController, IonRouterOutlet, ToastController, PopoverController, ActionSheetController } from '@ionic/angular';
import { FullScreenViewerService } from 'src/app/services/full-screen-viewer.service';
import { SupplementProtocolSettingsPage } from './supplement-protocol-settings/supplement-protocol-settings.page';
import { Organization, SupplementProtocol, ISupplementProtocolDetail, User, Supplement, SupplementCategory, SupplementAttachment } from 'src/app/models';
import { NotesPage } from 'src/app/components/notes/notes.page';
import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import moment from 'src/app/utils/moment';
import * as Sentry from "@sentry/angular-ivy";
import { Browser } from '@capacitor/browser';
import { SupplementSelectComponent } from 'src/app/modals/supplement-select/supplement-select.component';
import { SupplementCategorySelectComponent } from 'src/app/modals/supplement-category-select/supplement-category-select.component';
import { getProgramDatePopoverSide, ProgramDatePopoverComponent } from 'src/app/components/program-date-popover/program-date-popover.component';
import { isAccountDeactivatedForBillingStatus } from 'src/app/shared/billing/organization-status-policy';

@Component({
  selector: 'app-supplement-protocol-builder',
  templateUrl: './supplement-protocol-builder.page.html',
  styleUrls: ['./supplement-protocol-builder.page.scss'],
})
export class SupplementProtocolBuilderPage implements OnInit, AfterViewInit {
  @Input() comparePaneEmbedded = false;
  @Output() comparePaneClose = new EventEmitter<any>();

  @Input() modalContext?: {
    supplementProtocolUser?: string;
    id?: string;
    mode?: 'protocol' | 'category';
    type?: 'user' | 'library';
    duplicate?: string;
  };

  public actionSheet: HTMLIonActionSheetElement;
  public activeCategory: any = { supplements: [] };
  public activeCategoryIndex: number;
  public activeSupplementProtocol: ISupplementProtocolDetail = new SupplementProtocol({ notes: [] }).cloneActive();
  public activeUser: User;
  public configSet: boolean = false;
  public dragDisabled = false;
  public foods: any = {};
  public hasDraft = false;
  public newSetType: string = null;
  public isPremium = false;
  public programUser: string;
  public programId: string;
  public programMode: "protocol" | "category";
  public programType: "user" | "library";
  public saveMessageEnabled = true;
  public setSelectOptions = {
    header: "Select Set Type"
  }
  public setTypes: any;
  public supplementProtocol: SupplementProtocol;
  public organization: Organization;
  public versionHistory = [];
  /** Target supplement index while picking a file from the hidden file input. */
  public protocolAttachmentPickIndex: number | null = null;
  public desktopDetailsExpanded = new Set<number>();
  public mobileDetailsExpanded = new Set<number>();
  public isCategoryPopoverOpen = false;

  /**
   * Live reference to protocols[0].categories for the template.
   * Do not use a getter that calls ensure*() — that mutates during change detection and can freeze the app.
   */
  public categoryTabs: any[] = [];

  private refreshCategoryTabs(): void {
    this.ensureProtocolCategoriesStructure();
    this.categoryTabs = this.activeSupplementProtocol.protocols[0].categories;
  }

  private ensureProtocolCategoriesStructure(): void {
    if (!this.activeSupplementProtocol) {
      return;
    }
    if (!Array.isArray(this.activeSupplementProtocol.protocols) || this.activeSupplementProtocol.protocols.length === 0) {
      this.activeSupplementProtocol.protocols = [{ categories: [] }];
      return;
    }
    const row = this.activeSupplementProtocol.protocols[0];
    if (!row || typeof row !== 'object') {
      this.activeSupplementProtocol.protocols[0] = { categories: [] };
      return;
    }
    if (!Array.isArray(row.categories)) {
      row.categories = [];
    }
  }

  @ViewChild(IonSelect, { read: IonSelect }) select: IonSelect;

  constructor(
    private actionSheetCtrl: ActionSheetController,
    private alertController: AlertController,
    private appService: AppService,
    private cameraService: CameraService,
    private fullScreenViewer: FullScreenViewerService,
    private helperService: HelperService,
    private modalCtrl: ModalController,
    private organizationService: OrganizationService,
    private popoverCtrl: PopoverController,
    @Optional() private routerOutlet: IonRouterOutlet,
    private storageService: StorageService,
    private supplementService: SupplementService,
    private supplementProtocolService: SupplementProtocolService,
    private toastCtrl: ToastController,
    private translateConfig: TranslateConfigService,
    private userService: UserService
  ) {
    this.activeUser = this.userService.getAppUser();
    this.organization = organizationService.getAppOrganization();
  }

  ngOnInit() {
    if (this.routerOutlet) {
      this.routerOutlet.swipeGesture = false;
    }
    this.refreshCategoryTabs();
    this.storageService.get("supplementProtocolBuilderMessageDisabled").then((val) => {
      if(val === true) {
        this.saveMessageEnabled = false;
      }
    }); 
  }

  /** Supplement protocol builder is only entered via `SupplementProtocolBuilderModalService` + `modalContext`. */
  private buildProgramEntrySource(): Record<string, any> {
    return { ...((this.modalContext || {}) as Record<string, any>) };
  }

  async ionViewDidEnter() {
    if (this.comparePaneEmbedded) return;
    await this.runSupplementBuilderEntry();
  }

  ngAfterViewInit(): void {
    if (this.comparePaneEmbedded) {
      void this.runSupplementBuilderEntry();
    }
  }

  private dismissBuilder(data?: any): void {
    if (this.comparePaneEmbedded) {
      this.comparePaneClose.emit(data);
      return;
    }
    void this.modalCtrl.dismiss(data);
  }

  private async runSupplementBuilderEntry() {
    if (isAccountDeactivatedForBillingStatus(this.organization?.status)) {
      await this.appService.showToastWithDuration(this.translateConfig.t('general.accountDeactivatedCannotEdit'), null, "danger");
      this.dismissBuilder();
      return;
    }

    const sourceParams = this.buildProgramEntrySource();
    this.programUser = sourceParams.supplementProtocolUser;
    this.programId = sourceParams.id || "add";
    this.programMode = sourceParams.mode || "protocol";
    this.programType = sourceParams.type || "user";
    const duplicateId = sourceParams.duplicate;
    if (!this.programId || this.programId === "add") {
      if (duplicateId) {
        await this.appService.presentLoading();
        try {
          let supplementProtocol = this.programType === "library"
            ? await this.supplementProtocolService.getOrganizationSupplementProtocolAsPromise(this.organization.uid, duplicateId)
            : await this.supplementProtocolService.getSupplementProtocolAsPromise(this.programUser, duplicateId);
          this.activeSupplementProtocol = supplementProtocol.hasDraft() ? new SupplementProtocol(supplementProtocol.cloneDraft()) : new SupplementProtocol(supplementProtocol.cloneActive());
          this.activeSupplementProtocol.title = this.activeSupplementProtocol.title;
          delete this.activeSupplementProtocol.startDate;
          delete this.activeSupplementProtocol.endDate;
          this.refreshCategoryTabs();
        } catch (error) {
          Sentry.captureException(error.originalError || error);
          this.appService.showToastWithDuration("Unable to duplicate meal plan. An unknown error occurred.");
        } finally {
          setTimeout(() => {
            this.appService.hideLoading();
          }, 10);
        }
      } else {
        if(this.organization && this.programMode !== "category") {
          const DS = await this.organizationService.getDefaultSupplementProtocol(this.organization.uid);
          if (DS) {
            this.ensureProtocolCategoriesStructure();
            this.activeSupplementProtocol.protocols[0].categories = this.helperService.copyObject(DS);
          }
        }
        if(this.programMode === "category") {
          this.ensureProtocolCategoriesStructure();
          this.activeSupplementProtocol.protocols[0].categories = [];
          this.activeSupplementProtocol.protocols[0].categories.push({ supplements: [] });
        }
      }
      delete this.activeSupplementProtocol.createdBy;
      delete this.activeSupplementProtocol.creationDate;
      // Here we set premium for tracking users
      if(!this.organization || !this.organization.isUserTrainer(this.activeUser.uid)) {
        this.isPremium = true;
        this.activeSupplementProtocol.isPremium = true;
      }
      const defaultStartTime: string | null = this.organization?.getSetting("programs")?.defaultStartTime || null;
      const startMoment = (moment as any)();
      if (defaultStartTime) {
        const [hourStr] = defaultStartTime.split(":");
        const hour = parseInt(hourStr, 10);
        if (!Number.isNaN(hour)) {
          startMoment.hour(hour).minute(0).second(0).millisecond(0);
        }
      }
      this.activeSupplementProtocol.startDate = startMoment.toISOString();
      this.activeSupplementProtocol.startDateHidden = this.organization?.getSetting("programs")?.startDateHidden || false;
      this.activeSupplementProtocol.endDateHidden = this.organization?.getSetting("programs")?.endDateHidden || false;
      this.activeSupplementProtocol.notes = this.activeSupplementProtocol.notes && this.activeSupplementProtocol.notes.length > 0 ? this.activeSupplementProtocol.notes : (this.organization?.settings?.programs?.supplementProtocols?.defaultNotes || []);
      this.refreshCategoryTabs();
      await this.showProtocolOptions();
    } else {
      this.configSet = true;
      this.loadSupplementProtocol();
    }
  }

  async loadSupplementProtocol() {
    await this.appService.presentLoading();
    if(this.programType === "user") {
      this.supplementProtocol = await this.supplementProtocolService.getSupplementProtocolAsPromise(this.programUser, this.programId);
    } else {
      if(this.programMode === "protocol") {
        this.supplementProtocol = await this.supplementProtocolService.getOrganizationSupplementProtocolAsPromise(this.organization.uid, this.programId);
      } else {
        const category = await this.supplementProtocolService.getOrganizationSupplementCategoryAsPromise(this.organization.uid, this.programId);
        this.supplementProtocol = new SupplementProtocol({
          title: category.title || null,
          protocols: [{
            categories: [{
              supplements: category.supplements || [],
              notes: category.notes || null,
            }]
          }]
        });
      }
    }
    let activeSupplementProtocol = this.supplementProtocol.hasDraft() ? this.supplementProtocol.cloneDraft() : this.supplementProtocol.cloneActive();
    this.hasDraft = this.supplementProtocol.published && this.supplementProtocol.hasDraft();
    if (activeSupplementProtocol && activeSupplementProtocol.protocols && activeSupplementProtocol.protocols.length > 0 && activeSupplementProtocol.protocols[0].categories && activeSupplementProtocol.protocols[0].categories.length > 0) {
      this.activeSupplementProtocol = activeSupplementProtocol;
      this.ensureProtocolCategoriesStructure();
      this.activeCategory = this.activeSupplementProtocol.protocols[0].categories[0];
      this.activeCategoryIndex = 0;
      const toSave = this.helperService.copyObject(this.activeSupplementProtocol);
      this.versionHistory.push(toSave);
    }
    this.refreshCategoryTabs();
    await this.appService.hideLoading();
  }

  async addSupplement() {
    this.activeCategory.supplements = this.activeCategory.supplements || [];
    this.activeCategory.supplements.push({});
    this.clearDesktopSupplementExpandedRows();
    this.clearMobileSupplementExpandedRows();
    this.saveSupplementProtocol();
  }

  /**
   * Attachment metadata only (same storage URLs). Does not upload or duplicate files in Storage.
   */
  private cloneSupplementAttachmentDefinitions(
    attachments: Record<string, Partial<SupplementAttachment>> | undefined | null,
  ): Record<string, SupplementAttachment> | undefined {
    if (!attachments || typeof attachments !== 'object') {
      return undefined;
    }
    const out: Record<string, SupplementAttachment> = {};
    for (const key of Object.keys(attachments)) {
      const a = attachments[key];
      if (!a || typeof a !== 'object') {
        continue;
      }
      const { url, fileName, type, creationDate } = a;
      if (typeof url !== 'string' || !url || typeof fileName !== 'string' || !fileName || !type) {
        continue;
      }
      if (type !== 'image' && type !== 'video' && type !== 'file' && type !== 'audio') {
        continue;
      }
      const entry: SupplementAttachment = { url, fileName, type };
      if (typeof creationDate === 'string' && creationDate) {
        entry.creationDate = creationDate;
      }
      out[key] = entry;
    }
    return Object.keys(out).length ? out : undefined;
  }

  /** Strips library document ids and keeps attachment refs as metadata-only copies. */
  private protocolSupplementFromLibrarySource(supplement: Supplement): Record<string, unknown> {
    if (supplement == null || typeof supplement !== 'object') {
      return {};
    }
    const copied = this.helperService.copyObject(supplement) as Record<string, unknown>;
    delete copied.id;
    delete copied.uid;
    const att = this.cloneSupplementAttachmentDefinitions(copied.attachments as Record<string, Partial<SupplementAttachment>>);
    if (att) {
      copied.attachments = att;
    } else {
      delete copied.attachments;
    }
    return copied;
  }

  async selectSupplement() {
    if(["standard",  "pro", "platinum"].indexOf(this.organization.plan) > -1) {
      const modal = await this.modalCtrl.create({
        component: SupplementSelectComponent,
        componentProps: {
          organizationId: this.organization.uid
        }
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === "selected" && data.supplements) {
        this.activeCategory.supplements = this.activeCategory.supplements || [];
        for (const supplement of data.supplements) {
          this.activeCategory.supplements.push(this.protocolSupplementFromLibrarySource(supplement as Supplement));
        }
        this.saveSupplementProtocol();
      }
    } else {
      this.appService.showUpgradeModal()
    }
  }

  async duplicateSupplement(supplement, index) {
    this.activeCategory.supplements.splice(index, 0, this.helperService.copyObject(supplement));
    this.saveSupplementProtocol();
  }

  removeSupplement(index) {
    this.activeCategory.supplements.splice(index, 1);
    this.clearDesktopSupplementExpandedRows();
    this.clearMobileSupplementExpandedRows();
    this.saveSupplementProtocol();
  }

  isDesktopDetailsOpen(index: number): boolean {
    return this.desktopDetailsExpanded.has(index);
  }

  toggleDesktopDetails(index: number): void {
    this.desktopDetailsExpanded = this.toggleIndexSet(this.desktopDetailsExpanded, index);
  }

  isMobileDetailsOpen(index: number): boolean {
    return this.mobileDetailsExpanded.has(index);
  }

  toggleMobileDetails(index: number): void {
    this.mobileDetailsExpanded = this.toggleIndexSet(this.mobileDetailsExpanded, index);
  }

  hasDesktopDetailsData(supplement: Record<string, unknown>): boolean {
    const description = supplement?.description;
    const brand = supplement?.brand;
    const url = supplement?.url;
    return (typeof description === 'string' && description.trim().length > 0)
      || (typeof brand === 'string' && brand.trim().length > 0)
      || (typeof url === 'string' && url.trim().length > 0)
      || this.supplementAttachmentsList(supplement).length > 0;
  }

  private toggleIndexSet(source: Set<number>, index: number): Set<number> {
    const next = new Set(source);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    return next;
  }

  private clearDesktopSupplementExpandedRows(): void {
    this.desktopDetailsExpanded = new Set<number>();
  }

  private clearMobileSupplementExpandedRows(): void {
    this.mobileDetailsExpanded = new Set<number>();
  }

  private static readonly EMPTY_ATTACHMENT_ROWS: { id: string; url: string; fileName: string; type: string }[] = [];

  supplementAttachmentsList(supplement: Record<string, unknown>): { id: string; url: string; fileName: string; type: string }[] {
    const raw = supplement?.attachments as Record<string, SupplementAttachment> | undefined;
    if (!raw || typeof raw !== 'object') {
      return SupplementProtocolBuilderPage.EMPTY_ATTACHMENT_ROWS;
    }
    const keys = Object.keys(raw);
    if (keys.length === 0) {
      return SupplementProtocolBuilderPage.EMPTY_ATTACHMENT_ROWS;
    }
    return keys
      .map((id) => ({
        id,
        url: raw[id]?.url,
        fileName: raw[id]?.fileName,
        type: raw[id]?.type,
      }))
      .filter((a) => a.url && a.fileName && a.type);
  }

  trackByAttachmentEntryId(_: number, att: { id: string }): string {
    return att.id;
  }

  private uniqueProtocolAttachmentName(fromName: string, i = 0): string {
    const name = fromName || 'file';
    const dot = name.lastIndexOf('.');
    const ext = dot > -1 ? name.substr(dot + 1) : 'bin';
    return i === 0 ? `${Date.now()}.${ext}` : `${Date.now()}_${i}.${ext}`;
  }

  private newProtocolAttachmentKey(): string {
    return `p_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
  }

  openProtocolSupplementAttachmentPicker(supplementIndex: number) {
    this.protocolAttachmentPickIndex = supplementIndex;
    if (this.appService.isMobile()) {
      this.presentProtocolAttachmentActionSheet(supplementIndex);
    } else {
      const el = document.getElementById('protocolBuilderSupplementDesktopFile') as HTMLInputElement;
      el?.click();
    }
  }

  async presentProtocolAttachmentActionSheet(supplementIndex: number) {
    try {
      const sheet = await this.actionSheetCtrl.create({
        buttons: [
          { text: 'Take Photo', handler: () => { this.captureProtocolAttachment('image', supplementIndex); } },
          { text: 'Select Photo from Library', handler: () => { this.selectProtocolAttachment('image', supplementIndex); } },
          { text: 'Record Video', handler: () => { this.captureProtocolAttachment('video', supplementIndex); } },
          { text: 'Select Video from Library', handler: () => { this.selectProtocolAttachment('video', supplementIndex); } },
          {
            text: 'Attach File (PDF, Doc, etc.)',
            handler: () => {
              this.protocolAttachmentPickIndex = supplementIndex;
              document.getElementById('protocolBuilderSupplementUniversalFile')?.click();
            },
          },
          { text: this.translateConfig.t('general.cancel'), icon: 'close', role: 'cancel', handler: () => {} },
        ],
      });
      await sheet.present();
    } catch (error) {
      Sentry.captureException((error as any)?.originalError || error);
    }
  }

  async captureProtocolAttachment(type: 'image' | 'video', supplementIndex: number) {
    const supplement = this.activeCategory.supplements?.[supplementIndex];
    if (!supplement || !this.organization?.uid) {
      return;
    }
    try {
      if (type === 'image') {
        const attachmentData = await this.cameraService.captureImageAsBlob();
        if (attachmentData?.blob) {
          await this.appService.presentLoading();
          const meta = await this.supplementService.uploadProtocolEmbeddedSupplementAttachment(this.organization.uid, {
            name: this.uniqueProtocolAttachmentName(attachmentData.name),
            type: 'image',
            file: attachmentData.blob,
          });
          supplement.attachments = supplement.attachments || {};
          supplement.attachments[this.newProtocolAttachmentKey()] = meta;
          await this.saveSupplementProtocol();
        }
      } else {
        const attachmentData = await this.cameraService.captureVideoAsNative();
        if (attachmentData?.nativePath) {
          await this.appService.presentLoading();
          const meta = await this.supplementService.uploadProtocolEmbeddedSupplementAttachment(this.organization.uid, {
            name: this.uniqueProtocolAttachmentName(attachmentData.name),
            type: 'video',
            nativePath: attachmentData.nativePath,
          });
          supplement.attachments = supplement.attachments || {};
          supplement.attachments[this.newProtocolAttachmentKey()] = meta;
          await this.saveSupplementProtocol();
        }
      }
    } catch (error) {
      this.appService.showToastWithDuration((error as any)?.message || 'Unable to upload attachment.', null, 'danger');
      Sentry.captureException((error as any)?.originalError || error);
    } finally {
      await this.appService.hideLoading();
    }
  }

  async selectProtocolAttachment(type: 'image' | 'video', supplementIndex: number) {
    const supplement = this.activeCategory.supplements?.[supplementIndex];
    if (!supplement || !this.organization?.uid) {
      return;
    }
    try {
      const attachmentData = await this.cameraService.selectAttachmentAsNative(type);
      if (attachmentData?.nativePath) {
        await this.appService.presentLoading();
        const meta = await this.supplementService.uploadProtocolEmbeddedSupplementAttachment(this.organization.uid, {
          name: this.uniqueProtocolAttachmentName(attachmentData.name),
          type,
          nativePath: attachmentData.nativePath,
        });
        supplement.attachments = supplement.attachments || {};
        supplement.attachments[this.newProtocolAttachmentKey()] = meta;
        await this.saveSupplementProtocol();
      }
    } catch (error) {
      this.appService.showToastWithDuration((error as any)?.message || 'Unable to upload attachment.', null, 'danger');
      Sentry.captureException((error as any)?.originalError || error);
    } finally {
      await this.appService.hideLoading();
    }
  }

  async onProtocolSupplementFileInputChange(event: Event) {
    const input = event.target as HTMLInputElement;
    const idx = this.protocolAttachmentPickIndex;
    this.protocolAttachmentPickIndex = null;
    const supplement = idx != null ? this.activeCategory.supplements?.[idx] : null;
    if (!supplement || !this.organization?.uid) {
      input.value = '';
      return;
    }
    const selectedFiles = input.files;
    if (!selectedFiles?.length) {
      input.value = '';
      return;
    }
    try {
      await this.appService.presentLoading();
      supplement.attachments = supplement.attachments || {};
      for (let index = 0; index < selectedFiles.length; index++) {
        const file = selectedFiles[index];
        const meta = await this.supplementService.uploadProtocolEmbeddedSupplementAttachment(this.organization.uid, {
          file,
          type: this.helperService.getFileType(file.type),
          name: this.uniqueProtocolAttachmentName(file.name, index),
        });
        supplement.attachments[this.newProtocolAttachmentKey()] = meta;
      }
      await this.saveSupplementProtocol();
    } catch (error) {
      this.appService.showToastWithDuration((error as any)?.message || 'Unable to upload attachment.', null, 'danger');
      Sentry.captureException((error as any)?.originalError || error);
    } finally {
      await this.appService.hideLoading();
      input.value = '';
    }
  }

  removeProtocolSupplementAttachment(supplement: Record<string, unknown>, attachmentId: string) {
    const att = supplement.attachments as Record<string, unknown> | undefined;
    if (att && attachmentId) {
      delete att[attachmentId];
      if (Object.keys(att).length === 0) {
        delete supplement.attachments;
      }
    }
    this.saveSupplementProtocol();
  }

  async showSupplementAttachmentPreview(url: string) {
    if (url == null) {
      return;
    }
    await this.fullScreenViewer.openFullScreenViewer([{ image: url, title: '' }], 0);
  }

  async goToAttachmentUrl(url: string) {
    if (!url) {
      return;
    }
    if (this.appService.isMobile()) {
      await Browser.open({ url });
    } else {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  }

  async saveSupplementToLibrary(supplement: Supplement) {
    if (!this.organization.uid) {
      return;
    }
    if (['standard', 'pro', 'platinum'].indexOf(this.organization.plan) === -1) {
      this.appService.showUpgradeModal();
      return;
    }
    await this.appService.presentLoading();
    try {
      const base = this.helperService.cleanObjectForFirebase(
        this.helperService.copyObject({
          title: supplement.title || null,
          brand: supplement.brand || null,
          dose: supplement.dose || null,
          timing: supplement.timing || null,
          url: supplement.url || null,
          description: supplement.description || null,
          createdBy: this.activeUser.uid,
          creationDate: (moment as any)().toISOString(),
          organization: this.organization.uid,
        }),
      );
      const res = await this.supplementService.createSupplement(base);
      const newId = res.key as string;
      const att = this.cloneSupplementAttachmentDefinitions(
        supplement.attachments as Record<string, Partial<SupplementAttachment>>,
      );
      if (att && Object.keys(att).length) {
        await this.supplementService.updateSupplement(newId, { attachments: att });
      }
      await this.appService.showToastWithDuration(this.translateConfig.t('toasts.savedSuccess', { type: this.translateConfig.t('general.supplementProtocol') }), null, 'success');
    } catch (error) {
      await this.appService.showToastWithDuration('Unable to save supplement to library', null, 'danger');
      Sentry.captureException((error as any)?.originalError || error);
    } finally {
      this.appService.hideLoading();
    }
  }

  addCategory(title: string, supplements = [], notes?: string) {
    this.ensureProtocolCategoriesStructure();
    const supplementsForProtocol = (supplements || [])
      .filter((s) => s != null && typeof s === 'object')
      .map((s) => this.protocolSupplementFromLibrarySource(s as Supplement));
    this.activeSupplementProtocol.protocols[0].categories.push({
      title: title || null,
      supplements: supplementsForProtocol,
      notes: notes || null,
    });
    this.refreshCategoryTabs();
    this.saveSupplementProtocol();
  }

  setActiveCategory(index: number) {
    this.ensureProtocolCategoriesStructure();
    const categories = this.activeSupplementProtocol.protocols[0].categories;
    if (index < 0 || index >= categories.length) {
      return;
    }
    this.activeCategory = categories[index];
    this.activeCategoryIndex = index;
  }

  openCategoryDrawer() {
    this.isCategoryPopoverOpen = !this.isCategoryPopoverOpen;
  }

  onCategorySelected(index: number) {
    this.setActiveCategory(index);
  }

  selectCategoryForActions(index: number): void {
    this.setActiveCategory(index);
  }

  editCategoryFromDrawer(index: number): void {
    this.selectCategoryForActions(index);
    this.editCategoryTitle();
  }

  duplicateCategoryFromDrawer(category: any, index: number): void {
    this.selectCategoryForActions(index);
    this.duplicateCategory(category, index);
  }

  saveCategoryToLibraryFromDrawer(category: any, index: number): void {
    this.selectCategoryForActions(index);
    this.saveCategoryToLibrary(category);
  }

  deleteCategoryFromDrawer(index: number): void {
    this.selectCategoryForActions(index);
    this.deleteCategory(index);
  }

  duplicateCategory(category, index) {
    this.ensureProtocolCategoriesStructure();
    this.activeSupplementProtocol.protocols[0].categories.splice(index + 1, 0, {
      ...this.helperService.copyObject(category),
      title: category.title
    });
    this.setActiveCategory(index + 1);
    this.refreshCategoryTabs();
    this.saveSupplementProtocol();
  }

  async saveCategoryToLibrary(supplementCategory: Partial<SupplementCategory>) {
    if(this.organization?.uid) {
      if(["standard", "pro", "platinum"].indexOf(this.organization.plan) > -1) {
        const toSave = {
          supplements: supplementCategory.supplements || null,
          title: supplementCategory.title || null,
          notes: supplementCategory.notes || null
        };
        await this.supplementProtocolService.publishOrganizationSupplementCategory(this.organization.uid, null, toSave, this.activeUser.uid);
        await this.appService.showToastWithDuration("Category successfully saved to library", null, "success")
      } else {
        this.appService.showUpgradeModal();
      }
    }
  }

  deleteCategory(index) {
    this.ensureProtocolCategoriesStructure();
    const categories = this.activeSupplementProtocol.protocols[0].categories;
    categories.splice(index, 1);
    if (categories.length === 0) {
      this.activeCategory = { supplements: [] };
      this.activeCategoryIndex = undefined;
    } else {
      const nextIndex = Math.min(index, categories.length - 1);
      this.setActiveCategory(Math.max(0, nextIndex));
    }
    this.refreshCategoryTabs();
    this.saveSupplementProtocol();
  }

  private async presentCategoryTitleRequiredAlert() {
    const failAert = await this.alertController.create({
      header: 'Category Title Missing',
      message: 'You must enter a category title',
      buttons: ['OK'],
    });
    await failAert.present();
  }

  async newCategory() {
    this.ensureProtocolCategoriesStructure();
    const alert = await this.alertController.create({
      header: 'Category Title',
      inputs: [
        {
          name: 'categoryTitle',
          type: 'text',
          placeholder: 'e.g. Vitamins / Minerals'
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: (values) => {
            const raw = values?.categoryTitle;
            const title = typeof raw === 'string' ? raw.trim() : '';
            if (!title) {
              void this.presentCategoryTitleRequiredAlert();
              return false;
            }
            this.addCategory(title);
            this.setActiveCategory(this.categoryTabs.length - 1);
            return true;
          }
        }
      ]
    });
    await alert.present();
  }

  async selectCategory() {
    if(["standard",  "pro", "platinum"].indexOf(this.organization.plan) > -1) {
      const modal = await this.modalCtrl.create({
        component: SupplementCategorySelectComponent,
        componentProps: {}
      });
      await modal.present();
      const { data, role } = await modal.onWillDismiss();
      if (role === "selected" && data.data) {
        this.addCategory(data.data.title || null, data.data.supplements || [], data.data.notes || null);
        this.setActiveCategory(this.categoryTabs.length - 1);
      }
    } else {
      this.appService.showUpgradeModal();
    }
  }

  async editCategoryTitle() {
    let value = this.activeCategory.title;
    const alert = await this.alertController.create({
      header: 'Edit Category Title',
      inputs: [
        {
          name: 'planTitle',
          type: 'text',
          value: value,
          placeholder: `e.g. Non Workout Days`,
        }
      ],
      buttons: [
        {
          text: this.translateConfig.t('general.cancel'),
          role: 'cancel',
          cssClass: 'secondary',
          handler: () => { }
        }, {
          text: this.translateConfig.t('general.ok'),
          handler: async (values) => {
            let planTitle = values.planTitle
            if (planTitle) {
              this.activeCategory.title = planTitle;
              this.saveSupplementProtocol();
            } else {
              return
            }
          }
        }
      ]
    });
    await alert.present();
  }

  getProtocolSettings() {
    this.ensureProtocolCategoriesStructure();
    return {
      title: this.activeSupplementProtocol.title || null,
      startDate: this.activeSupplementProtocol.startDate || null,
      startDateHidden: this.helperService.valueExists(this.activeSupplementProtocol.startDateHidden)? this.activeSupplementProtocol.startDateHidden : false,
      endDate: this.activeSupplementProtocol.endDate || null,
      endDateHidden: this.helperService.valueExists(this.activeSupplementProtocol.endDateHidden)? this.activeSupplementProtocol.endDateHidden : false,
      protocols: this.activeSupplementProtocol.protocols
    }
  }

  setProtocolSettings(obj) {
    Object.assign(this.activeSupplementProtocol, obj);
  }

  async showProtocolOptions() {
    let action = this.configSet ? "edit" : "add";
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: SupplementProtocolSettingsPage,
      componentProps: {
        action: action,
        supplementProtocol: this.getProtocolSettings(),
        type: this.programType,
        mode: this.programMode,
        premium: this.isPremium
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === 'submitted') {
      this.setProtocolSettings(data.data);
      this.refreshCategoryTabs();
      if (this.categoryTabs.length > 0) {
        this.setActiveCategory(0);
      } else {
        this.activeCategory = { supplements: [] };
        this.activeCategoryIndex = undefined;
      }
      this.configSet = true;
      this.saveSupplementProtocol();
    } else if (!this.configSet) {
      setTimeout(() => {
        this.dismissBuilder();
      }, 500);
    } else { }
  }

  async showNotes() {
    let notes = this.activeSupplementProtocol.notes;
    const modal = await this.modalCtrl.create({
      backdropDismiss: false,
      component: NotesPage,
      componentProps: {
        defaultNotes: this.organization?.settings?.programs?.supplementProtocols?.defaultNotes,
        action: "edit",
        notes: notes
      }
    });
    await modal.present();
    const { data } = await modal.onWillDismiss();
    if (data && data.status === "submitted") {
      this.activeSupplementProtocol.notes = data.data;
      this.saveSupplementProtocol();
    }
  }

  disableSaveMessage() {
    this.storageService.set("supplementProtocolBuilderMessageDisabled", true);
    this.saveMessageEnabled = false;
  }

  async showSaveMessage() {
    if(!this.saveMessageEnabled) {
      return
    }
    const toast = await this.toastCtrl.create({
      message: "Your supplement protocol has been automatically saved",
      buttons: [
        {
          text: "Don't show again",
          role: 'cancel',
          handler: () => {
            this.disableSaveMessage();
          }
        }
      ],
      position: 'bottom',
      color: "success",
      duration: 3000,
    });
    toast.present();
  }

  async saveSupplementProtocol() {
    try {
      this.ensureProtocolCategoriesStructure();
      let res;
      const toSave = this.helperService.copyObject(this.activeSupplementProtocol);
      this.versionHistory.push(toSave);
      if(this.programType === "user") {
        res = await this.supplementProtocolService.saveUserSupplementProtocol(this.organization?.uid, this.programUser, this.programId, toSave, this.activeUser.uid);
      } else {
        if(this.programMode === "category") {
          return
        }
        res = await this.supplementProtocolService.saveOrganizationSupplementProtocol(this.organization.uid, this.programId, toSave, this.activeUser.uid);
      }
      if (this.programId === "add") {
        this.programId = res.key;
      }
      this.showSaveMessage();
      if(this.supplementProtocol?.published) {
        this.hasDraft = true;
      }
    } catch (error) {
      console.log(error);
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    }
  }

  async publishSupplementProtocol() {
    await this.appService.presentLoading();
    try {
      let programValue;
      if(this.programType === "user") {
        programValue = await this.supplementProtocolService.publishUserSupplementProtocol(this.organization?.uid, this.programUser, this.programId, this.activeSupplementProtocol, this.activeUser.uid);
      } else {
        if(this.programMode === "protocol") {
          programValue = await this.supplementProtocolService.publishOrganizationSupplementProtocol(this.organization.uid, this.programId, this.activeSupplementProtocol, this.activeUser.uid);
        } else {
          programValue = await this.supplementProtocolService.publishOrganizationSupplementCategory(this.organization.uid, this.programId, {title: this.activeSupplementProtocol.title, supplements: this.activeCategory?.supplements || [], notes: this.activeCategory.notes || null}, this.activeUser.uid);
        }
      }
      if(this.programId === "add") {
        this.programId = programValue.key;
      }

      this.dismissBuilder({ status: 'published', programId: this.programId });

      this.appService.showToastWithDuration("Successfully published supplement protocol", null, "success");     
    } catch (error) {
      console.log(error);
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    } finally {
      setTimeout(() => {
        this.appService.hideLoading();
      }, 100);
    }
  }

  undoRevision() {
    const historyIndex = this.versionHistory.length - 1;
    this.activeSupplementProtocol = this.versionHistory[historyIndex - 1];
    this.refreshCategoryTabs();
    if (this.categoryTabs.length > 0) {
      const idx = Math.min(this.activeCategoryIndex ?? 0, this.categoryTabs.length - 1);
      this.setActiveCategory(Math.max(0, idx));
    } else {
      this.activeCategory = { supplements: [] };
      this.activeCategoryIndex = undefined;
    }
    this.versionHistory.splice(historyIndex -1, 2);
    this.saveSupplementProtocol()
  }

  async revertDraft() {
    let activeSupplementProtocol =  this.supplementProtocol.cloneActive();
    if (activeSupplementProtocol && activeSupplementProtocol.protocols && activeSupplementProtocol.protocols.length > 0 && activeSupplementProtocol.protocols[0].categories && activeSupplementProtocol.protocols[0].categories.length > 0) {
      this.activeSupplementProtocol = activeSupplementProtocol;
      this.activeCategory = this.activeSupplementProtocol.protocols[0].categories[0];
      this.activeCategoryIndex = 0;
      const toSave = this.helperService.copyObject(this.activeSupplementProtocol);
      this.versionHistory = [toSave];
    }
    this.refreshCategoryTabs();

    try {
      if(this.programType === "user") {
        await this.supplementProtocolService.publishUserSupplementProtocol(this.organization.uid, this.programUser, this.programId, this.activeSupplementProtocol, this.activeUser.uid, false);
      } else {
        await this.supplementProtocolService.publishOrganizationSupplementProtocol(this.organization.uid, this.programId, this.activeSupplementProtocol, this.activeUser.uid);
      }
     this.appService.showToastWithDuration("Supplement Protocol successfully reverted", null, "success");      
     this.hasDraft = false;
    } catch (error) {
      Sentry.captureException(error.originalError || error);
      this.appService.showToastWithDuration(this.translateConfig.t('general.unexpectedError'));
    }
  }

  isTrainer() {
    return this.activeUser && this.organization.isUserTrainer(this.activeUser.uid);
  }

  reorderSupplement(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.activeCategory.supplements, event.previousIndex, event.currentIndex);
    this.clearDesktopSupplementExpandedRows();
    this.saveSupplementProtocol();
  }

  reorderSupplementM(event) {
    const itemMove = this.activeCategory.supplements.splice(event.detail.from, 1)[0];
    this.activeCategory.supplements.splice(event.detail.to, 0, itemMove);
    this.clearMobileSupplementExpandedRows();
    this.saveSupplementProtocol();
    event.detail.complete();
  }

  drop(event: CdkDragDrop<string[]>) {
    this.ensureProtocolCategoriesStructure();
    moveItemInArray(this.activeSupplementProtocol.protocols[0].categories, event.previousIndex, event.currentIndex);
    this.setActiveCategory(event.currentIndex);
    this.refreshCategoryTabs();
    this.saveSupplementProtocol();
  }

  disableDrag() {
    this.dragDisabled = true;
  }

  enableDrag() {
    this.dragDisabled = false;
  }

  goBack() {
    this.dismissBuilder();
  }

  async editDate(event, type: "startDate" | "endDate") {
    const popover = await this.popoverCtrl.create({
      component: ProgramDatePopoverComponent,
      event: event,
      cssClass: 'popover-full',
      side: getProgramDatePopoverSide(event),
      componentProps: {
        initial: this.activeSupplementProtocol[type],
        clear: type === "endDate",
        includeTime: true,
        dateType: type,
      }
    });
    await popover.present();
    const { role, data } = await popover.onDidDismiss();
    if(role === "cleared" || role === "set") {
      const toUpdate = data || null;
      this.activeSupplementProtocol[type] = toUpdate;
      this.saveSupplementProtocol();
    }
  }
}
